import { BottomNavigation, Typography } from "@mui/material";
import { useRouter } from "next/router";
import { useGetOrderByIdQuery } from "../../../store";
import { getTimeAndDateStringFromEPOC } from "../../../utils";
import { Bullet } from "../../atoms";
import withAuth from "../../hoc/withAuth";
import { useScreenSize } from "../../hooks/useScreenSize";
import { AuthLayout } from "../../layouts";
import { PriceDetailsGrid } from "../../molecules/PriceDetailsGrid";
import { AddressCard, OrderDetailsCard } from "../../organisms";
import { CompletePaymentButtons } from "./CompletePaymentButtons";

export const OrderDetails = withAuth<{}>(() => {
  const { query } = useRouter();
  const { data: order } = useGetOrderByIdQuery(query.id as string);
  const { isMobile } = useScreenSize();

  const getTime = (createdAt: number) => {
    const { date, time } = getTimeAndDateStringFromEPOC(createdAt);
    return `Ordered on ${date} at ${time}`;
  };

  const getTrackingUrl = (itemId: string, variantId: string) => {
    if (!order) return undefined;
    const fulfilmentInfo = order.fulfillments.find(
      f => f.itemId === itemId && f.variantId === variantId
    );
    if (!fulfilmentInfo) return undefined;
    return fulfilmentInfo.trackingUrl;
  };

  if (!order) return null;

  return (
    <AuthLayout
      title={`#${order.orderNumber}`}
      subtitle={
        <Typography color="text.secondary" variant="body2">
          Total {order.totalProducts} product <Bullet />{" "}
          {getTime(order.createdAt)}
        </Typography>
      }
      action={
        order.finentialStatus === "CREATED" && !isMobile ? (
          <CompletePaymentButtons order={order} />
        ) : null
      }>
      <>
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="col-span-1 flex flex-col gap-8 lg:col-span-2">
              {order.items.map(product => {
                return (
                  <div key={product.id}>
                    <OrderDetailsCard
                      product={product}
                      currency={order.currency}
                      trackingUrl={getTrackingUrl(
                        product.id,
                        product.variantId
                      )}
                    />
                  </div>
                );
              })}
            </div>
            <div className="col-span-1 flex flex-col gap-8">
              <PriceDetailsGrid
                data={[
                  {
                    label: "subTotal",
                    value: `${order.currency} ${order.priceData.subTotal}`,
                  },
                  {
                    label: "discount",
                    value: `${order.priceData.discount}%`,
                  },
                  {
                    label: "shippingCharges",
                    value: `${order.currency} ${order.priceData.shippinCharges}`,
                  },
                  {
                    label: "tax (Inclusive)",
                    value: `${order.currency} ${order.priceData.tax}`,
                  },
                ]}
              />
              <div className="flex flex-col gap-3">
                <Typography variant="subtitle1" fontWeight={700}>
                  Shipping address
                </Typography>
                <AddressCard address={order.shippingAddress} />
              </div>
              <div className="flex flex-col gap-3">
                <Typography variant="subtitle1" fontWeight={700}>
                  Billing address
                </Typography>
                <AddressCard address={order.shippingAddress} />
              </div>
            </div>
          </div>
        </div>
        {order.finentialStatus === "CREATED" && isMobile && (
          <BottomNavigation
            sx={{
              position: "sticky",
              bottom: 0,
              p: 0.5,
            }}>
            <CompletePaymentButtons order={order} />
          </BottomNavigation>
        )}
      </>
    </AuthLayout>
  );
});
